import { useEffect, useState, useCallback } from "react";
import { useOperator } from "@/context/OperatorContext";
import { opApi } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LogOut } from "lucide-react";
import { DayColumns } from "@/components/mc/appt/ApptCard";
import { ApptSearch } from "@/components/mc/appt/ApptSearch";
import { WalkInButton } from "@/components/mc/appt/WalkIn";
import { LeadsList } from "@/components/mc/cn/ConnectAdmin";
import { ProfileEditor } from "@/components/mc/cn/ProfileEditor";

function MyClients() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { opApi.get("/tenant/my-clients").then(({ data }) => setRows(data || [])).catch(() => {}); }, []);
  const list = rows.filter((r) => !q || `${r.name} ${r.phone} ${r.email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="my-clients">
      <Input className="rounded-none max-w-sm mb-3" placeholder="Search your clients…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="my-clients-search" />
      <div className="border border-border divide-y">
        {list.length === 0 && <div className="p-4 text-sm text-muted-foreground">No clients yet.</div>}
        {list.map((r, i) => (
          <div key={i} className="p-3 text-sm" data-testid={`my-client-${i}`}>
            <div className="font-medium">{r.name}</div>
            <div className="text-xs text-muted-foreground">{[r.phone, r.email].filter(Boolean).join(" · ")}</div>
            <div className="text-xs mt-1">{r.visits} visit{r.visits === 1 ? "" : "s"}{r.last_visit ? ` · last ${r.last_visit} (${r.last_service})` : ""}{r.next_visit ? ` · next ${r.next_visit}` : ""}</div>
            {r.notes.length > 0 && <div className="text-xs text-muted-foreground mt-1 italic">“{r.notes[r.notes.length - 1]}”</div>}
          </div>
        ))}
      </div>
    </div>
  );
}


function useMyLeadsAndProfile() {
  const [leads, setLeads] = useState([]);
  const [pro, setPro] = useState(null);
  const loadLeads = useCallback(() => opApi.get("/tenant/connect/leads").then(({ data }) => setLeads(data || [])).catch(() => {}), []);
  const loadPro = useCallback(() => opApi.get("/tenant/connect/profiles").then(({ data }) => setPro((data || [])[0] || false)).catch(() => {}), []);
  useEffect(() => { loadLeads(); loadPro(); }, [loadLeads, loadPro]);
  return { leads: <LeadsList leads={leads} pros={[]} canAssign={false} reload={loadLeads} />,
    profile: pro === false ? <p className="text-sm text-muted-foreground">No profile linked yet — ask your owner.</p> : pro ? <ProfileEditor pro={pro} canLock={false} onSaved={loadPro} /> : null };
}

function ConnectTabs() {
  const v = useMyLeadsAndProfile();
  return <>
    <TabsContent value="leads" className="mt-6">{v.leads}</TabsContent>
    <TabsContent value="profile" className="mt-6">{v.profile}</TabsContent>
  </>;
}

function MyJobs() {
  const [jobs, setJobs] = useState([]);
  const load = useCallback(() => opApi.get("/tenant/jobs").then(({ data }) => setJobs(data || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = (id, status) => opApi.patch(`/tenant/jobs/${id}/status`, { status }).then(() => { toast.success("Updated"); load(); }).catch((e) => toast.error(e.response?.data?.detail || "Couldn't update"));
  const list = jobs.filter((j) => ["scheduled", "in_progress", "completed"].includes(j.status)).sort((a, b) => `${a.visit?.date}${a.visit?.start}`.localeCompare(`${b.visit?.date}${b.visit?.start}`));
  return (
    <div className="border border-border divide-y" data-testid="my-jobs">
      {list.length === 0 && <div className="p-4 text-sm text-muted-foreground" data-testid="my-jobs-empty">No jobs assigned to you yet.</div>}
      {list.map((j) => (
        <div key={j.id} className="p-3 text-sm" data-testid={`my-job-${j.id}`}>
          <div className="flex justify-between gap-2"><div className="font-medium">{j.visit?.date} {j.visit?.start}–{j.visit?.end} · {j.customer?.name}</div><span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-secondary">{j.status.replace("_", " ")}</span></div>
          <div className="text-xs text-muted-foreground">{j.customer?.address}{j.customer?.phone ? ` · ${j.customer.phone}` : ""} · {j.ref}</div>
          <div className="text-xs mt-1">{j.category} — {j.description}</div>
          {j.internal_notes && <div className="text-xs mt-1 italic text-muted-foreground">Note: {j.internal_notes}</div>}
          <div className="flex gap-1 mt-2">
            {j.status === "scheduled" && <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => setStatus(j.id, "in_progress")} data-testid={`my-job-start-${j.id}`}>Start work</Button>}
            {j.status !== "completed" && <Button size="sm" className="rounded-none h-7 text-xs" onClick={() => setStatus(j.id, "completed")} data-testid={`my-job-complete-${j.id}`}>Complete</Button>}
          </div>
        </div>
      ))}
    </div>
  );
}

// Staff-only dashboard: their own calendar (+ shop calendar when the owner turns it on).
export default function StaffDashboard() {
  const { operator, logout } = useOperator();
  const [settings, setSettings] = useState(null);
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [view, setView] = useState("mine");
  const [appts, setAppts] = useState([]);
  const [tick, setTick] = useState(0);
  const shared = !!settings?.shared_calendar;
  const viewer = { manage: false, providerId: operator.provider_id, shared };

  useEffect(() => {
    opApi.get("/tenant/cart-type").then(({ data }) => setSettings(data.appt_settings)).catch(() => {});
    opApi.get("/tenant/services").then(({ data }) => setServices(data || [])).catch(() => {});
    opApi.get("/tenant/providers").then(({ data }) => setProviders(data || [])).catch(() => {});
  }, []);
  const load = useCallback(() => opApi.get("/tenant/appointments", { params: { date, view } }).then(({ data }) => setAppts(data || [])).catch(() => {}), [date, view]);
  useEffect(() => { load(); }, [load, tick]);
  const onStatus = (id, status) => opApi.patch(`/tenant/appointments/${id}`, { status }).then(() => { toast.success("Updated"); setTick((t) => t + 1); }).catch((e) => toast.error(e.response?.data?.detail || "Couldn't update"));
  const me = providers.find((p) => p.id === operator.provider_id);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6" data-testid="staff-dashboard">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            {operator.tenant?.logo_url && <img src={operator.tenant.logo_url} alt="" className="h-10 w-10 object-contain border" />}
            <div><h1 className="font-display text-2xl font-bold" data-testid="staff-shop-name">{operator.tenant?.name}</h1>
              <div className="text-xs text-muted-foreground" data-testid="staff-whoami">{operator.member_name || operator.user?.name} · Staff{me ? ` · ${me.name}` : " · not linked yet"}</div></div>
          </div>
          <Button variant="outline" className="rounded-none gap-1.5" onClick={logout} data-testid="staff-logout"><LogOut className="h-4 w-4" /> Sign out</Button>
        </div>
        {operator.tenant?.cart_flow === "quote" ? <MyJobs /> : (
        <Tabs defaultValue="calendar">
          <TabsList className="rounded-none">
            <TabsTrigger value="calendar" className="rounded-none" data-testid="staff-tab-calendar">Calendar</TabsTrigger>
            <TabsTrigger value="clients" className="rounded-none" data-testid="staff-tab-clients">My clients</TabsTrigger>
            {operator.tenant?.cart_flow === "call" && <TabsTrigger value="leads" className="rounded-none" data-testid="staff-tab-leads">My leads</TabsTrigger>}
            {operator.tenant?.cart_flow === "call" && <TabsTrigger value="profile" className="rounded-none" data-testid="staff-tab-profile">My profile</TabsTrigger>}
          </TabsList>
          <TabsContent value="calendar" className="mt-6 space-y-4">
            <ApptSearch viewer={viewer} onStatus={onStatus} refreshKey={tick} />
            <div className="flex items-center gap-2 flex-wrap">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-none w-auto" data-testid="staff-date" />
              {shared && (
                <div className="flex border border-border" data-testid="staff-view-toggle">
                  <button className={`px-3 h-9 text-sm ${view === "mine" ? "bg-primary text-primary-foreground" : ""}`} onClick={() => setView("mine")} data-testid="staff-view-mine">My calendar</button>
                  <button className={`px-3 h-9 text-sm ${view === "shop" ? "bg-primary text-primary-foreground" : ""}`} onClick={() => setView("shop")} data-testid="staff-view-shop">Shop calendar</button>
                </div>
              )}
              <div className="flex-1" />
              <WalkInButton services={services} providers={providers} viewer={viewer} onAdded={() => setTick((t) => t + 1)} />
            </div>
            <DayColumns providers={providers} appts={appts} viewer={viewer} onStatus={onStatus} />
          </TabsContent>
          <TabsContent value="clients" className="mt-6"><MyClients /></TabsContent>
          {operator.tenant?.cart_flow === "call" && <ConnectTabs />}
        </Tabs>
        )}
      </div>
    </div>
  );
}
